











































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Button, Checkbox, Form, FormItem, Option, Select } from 'element-ui';
import BillItemAutocomplete from '@/components/billItem/BillItemAutocomplete.vue';
import NullableInput from '@/components/element-ui/NullableInput.vue';
import NullableNumberInput from '@/components/element-ui/NullableNumberInput.vue';
import PartnerCreateSelector from '@/components/partner/PartnerCreateSelector.vue';

import {
  BillItemFields,
  BillItemInput,
  InquiryFields,
  PartnerFields,
  ShipmentFields,
  TaxTypeEnum
} from '@/types/graphql/schemaTypes';
import billItemTypesOptions from '@/constants/billItemTypes';

import { taxRate } from '@/constants/tax';
import currencyOptions from '@/constants/currencyOptions';

import { billItemService } from '@/services/BillItemService.ts';

import draggable from 'vuedraggable';
import get from 'lodash/get';

// gql
import fetchPartnersListQuery from '@/graphql/queries/partners/FetchPartnerList.gql';

interface BillItemTableInput extends BillItemInput {
  costJpy?: number;
  costWithTax?: number;
  margin?: number;
  totalAmount?: number;
  totalTax?: number;
  _new?: boolean;
  isPublic?: boolean;
}

enum DirectionEnum {
  'up',
  'down'
}

@Component({
  apollo: {
    partnerOptions: {
      query: fetchPartnersListQuery,
      variables() {
        return {
          pagination: {
            page: 1,
            per: 1000
          }
        };
      },
      update(data) {
        return data?.partners;
      },
      fetchPolicy: 'cache-and-network',
      error() {
        this.$store.dispatch('Alert/addAlert', {
          type: 'error',
          message: this.$t('error.fetch'),
          time: 2000
        });
      }
    }
  },
  components: {
    draggable,
    BillItemAutocomplete,
    NullableNumberInput,
    PartnerCreateSelector,
    'el-button': Button,
    'el-checkbox': Checkbox,
    'el-input': NullableInput,
    'el-form': Form,
    'el-form-item': FormItem,
    'el-option': Option,
    'el-select': Select
  }
})
export default class BillTable extends Vue {
  @Prop({ required: true }) shipment!: ShipmentFields | InquiryFields;

  private readonly currencyOptions = currencyOptions();
  private readonly billItemTypesOptions = billItemTypesOptions();
  private emptyBillItem: BillItemTableInput = {
    category: '',
    confirmed: false,
    cost: 0,
    currency: 'JPY',
    description: '',
    isPublic: true,
    isSection: false,
    order: null,
    partnerId: null,
    quantity: 1,
    taxRate: 0,
    taxType: TaxTypeEnum.no_tax,
    unitPrice: 0
  };

  private loading = false;
  private tableData: BillItemTableInput[] = this.generateBillItemInputs();
  private touched = false;
  private partnerOptions: PartnerFields[];

  private addBillItem(index: number | null) {
    const newObjectIndex = (index === null) ? this.tableData.length : index + 1;
    const newItem = {
      ...this.emptyBillItem,
      id: new Date().getTime().toString(),
      order: newObjectIndex,
      _new: true
    };

    this.tableData.splice(newObjectIndex, 0, newItem);
  }

  private addSection(index: number | null) {
    const newObjectIndex = (index === null) ? this.tableData.length : index + 1;
    const newItem = {
      id: new Date().getTime().toString(),
      description: '',
      order: newObjectIndex,
      isSection: true,
      isPublic: true,
      billItemType: 'sales',
      _new: true
    };

    this.tableData.splice(newObjectIndex, 0, newItem);
  }

  /**
   * from a given bill item section, will calculate total of item below it
   * @argument from : index of a bill item section
   * @argument column : disered subtotal
  */
  private calculateSubTotal(id: string, column: string): number {
    // remove deleted item
    const data = this.tableData.filter(data => !data._destroy);
    const from = data.findIndex(item => item.id === id);

    let total = 0;
    for (let index = from + 1 || 0; index < data.length; index += 1) {
      if (data[index].isSection) {
        break;
      }

      if (column === 'cost') {
        total += this.calculateUsdToJpy(data[index]) * (data[index].quantity || 0);
      } else {
        total += parseInt(get(data[index], column) || 0, 10);
      }
    }
    return total;
  }

  private calculateUsdToJpy(item: BillItemInput): number {
    const cost = item.cost || 0;
    const rate = this.shipment.usdJpyExchangeRate || 0;
    if (item.currency === 'USD') {
      // convert to JPY
      return Math.round((rate * 100 * cost) / 100);
    }
    return cost;
  }

  private generateBillItemInputs(): BillItemTableInput[] {
    if (this.shipment && this.shipment.billItems) {
      return this.shipment.billItems.map((item: BillItemFields, index: number) => {
        if (item.isSection) {
          return {
            description: item.description,
            id: item.id || null,
            isPublic: true,
            isSection: item.isSection,
            order: index,
            billItemType: item.billItemType
          };
        }
        return {
          category: item.category,
          confirmed: item.confirmed,
          cost: item.cost,
          costJpy: item.costJpy || 0,
          costWithTax: item.costWithTax,
          currency: item.currency || 'JPY',
          description: item.description,
          id: item.id || null,
          isPublic: !item.isPrivate,
          isSection: item.isSection || false,
          margin: item.margin,
          order: index,
          partnerId: item.partner?.id,
          quantity: item.quantity,
          taxRate: item.taxRate || this.shipment.taxRate || 0,
          taxType: item.taxType || TaxTypeEnum.no_tax,
          totalAmount: item.totalAmount || 0,
          totalTax: item.totalTax || 0,
          unitPrice: item.unitPrice
        };
      });
    }
    return [];
  }

  private onMoveCallback(evt: any) {
    const oldIndex = evt.moved.oldIndex;
    const newIndex = evt.moved.newIndex;

    let fromIndex = null;
    let toIndex = null;
    let direction: DirectionEnum = DirectionEnum.up;

    // if section move all belonging items with it
    if (evt.moved.element.isSection) {
      // moving up
      if (oldIndex > newIndex) {
        fromIndex = oldIndex + 1;
        toIndex = newIndex + 1;
      // moving down
      } else {
        fromIndex = oldIndex;
        toIndex = newIndex + 1;
        direction = DirectionEnum.down;
      }

      while (fromIndex < this.tableData.length) {
        // if it's a section and not deleted we exit the loop
        if (this.tableData[fromIndex].isSection && !this.tableData[fromIndex]._destroy) {
          break;
        }

        // move item
        const element = this.tableData[fromIndex];

        this.tableData.splice(fromIndex, 1);
        this.tableData.splice(toIndex, 0, element);

        if (direction === DirectionEnum.up) {
          toIndex += 1;
          fromIndex += 1;
        }
      }
    }
  }

  private remove(id: string) {
    const index = this.tableData.findIndex((data: BillItemTableInput) => {
      return data.id === id;
    });

    if (this.tableData[index]._new) {
      // remove newly created item
      this.tableData.splice(index, 1);
    } else {
      // flag deleted row with _destroy
      this.$set(this.tableData, index, { ...this.tableData[index], _destroy: true });
    }
    this.touched = true;
  }

  private reset() {
    this.tableData = this.generateBillItemInputs();
    // make sure the watcher on tableData doesn't trigger re set touched
    this.$nextTick(() => {
      this.touched = false;
    });
  }

  private setTax(id: string, taxedType: TaxTypeEnum) {
    const index = this.tableData.findIndex(item => item.id === id);

    if (taxedType !== TaxTypeEnum.no_tax) {
      this.tableData[index].taxRate = this.shipment.taxRate;
    } else {
      this.tableData[index].taxRate = 0;
    }
  }

  private rowTemplateUpdate(id: string, value) {
    if (!value) {
      return;
    };

    const index = this.tableData.findIndex(item => item.id === id);

    this.tableData[index].category = value.category;
    this.tableData[index].taxType = value.taxType;
    this.setTax(id, value.taxType);
  }

  private async submit() {
    const billItems: BillItemTableInput[] = this.tableData.map((bi: BillItemTableInput) => {
      // if new item remove temporary id and _new tag
      if (bi._new) {
        bi.id = null;
        delete bi._new;
      }
      // Operators want to put in a 0 after decimal and v-model.number calls parseFloat
      // after every input, so 1.0 becomes 1. :( So we set the input as a string, then call
      // parseFloat during the submit to change the quantity from a string to a float.
      return {
        id: bi.id || null,
        billId: bi.billId,
        billItemType: bi.billItemType,
        description: bi.description,
        currency: bi.currency,
        category: bi.category,
        unitPrice: bi.unitPrice,
        confirmed: bi.confirmed,
        cost: bi.cost,
        partnerId: bi.partnerId,
        quantity: !bi.isSection ? parseFloat(bi.quantity!.toString()) || 0 : 0,
        taxType: bi.taxType,
        taxRate: this.shipment.taxRate,
        order: bi.order,
        isPrivate: !bi.isPublic,
        isSection: bi.isSection,
        _destroy: bi._destroy
      };
    });

    this.loading = true;

    try {
      const response = await billItemService.upsertBillItems(
        billItems,
        this.shipment.id
      );

      if (response) {
        this.touched = false;
        this.$emit('refresh');
        this.$store.dispatch('Alert/addAlert', {
          type: 'success',
          message: this.$t('success.updated'),
          time: 2000
        });
      }
    } catch (error) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    } finally {
      this.loading = false;
    }
  }

  @Watch('shipment')
  private setData() {
    if (!this.touched) {
      this.reset();
    }
  }

  @Watch('tableData', { deep: true })
  private setTouched() {
    this.touched = true;
  }
}
